import React, { useEffect, useState } from 'react';

import { Field, FieldProps, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import classNames from 'classnames';

import { BtnOneLineBlue } from './buttons/BtnOneLineBlue';
import { FormInput } from './FormInput';
import { MobileInput } from './MobileInput';
import FormCheckbox from './FormCheckbox';
import FormSelect from './FormSelect';
import PromptMessage from './UI/PromptMessage';
import { getConsents, getConsentsSuccess } from '@/store/actions/consentsActions';
import { logException } from '@/utils/logger';
import { deepCompare, generateBffErrorMessageWithCode, stringValueByKey } from '@/utils/utils';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { getUserSuccess } from '@/store/actions/user';
import { useBffRequest } from '@/hooks/useBffRequest';
import BffApiService from '@/shared/bffApiService';
import {
  BffUser,
  BffAgcoUserDetailsResponse,
  BffUserAgcoVerifyUpdateResponse,
} from '@lucky7ventures/bff-types';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffApiError } from '@/models/bff-api-error';
import { setShowUserIsPepOrHioLogout } from '@/store/modal/actions';

interface FormValues {
  fullName: string;
  address: string;
  houseNumber: string;
  city: string;
  postCode: string;
  occupationId: string;
  industryId: string;
  mobile: {
    number: string;
    country: string;
    prefix: string;
    wholeNumber: string;
  };
  isNotPepOrHio: boolean;
}

const DetailsReverificationForm = ({
  user,
  additionalUserDetails,
  closeModal,
}: {
  user: BffUser;
  additionalUserDetails: BffAgcoUserDetailsResponse;
  closeModal: () => void;
}) => {
  const intl = useIntl();
  const absoluteNavigate = useAbsoluteNavigate();
  const dispatch = useDispatch();
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const {
    request: fetchIndustriesOccupationsRequest,
    data: fetchIndustriesAndOccupationsData,
    loading: fetchIndustriesAndOccupationsLoading,
  } = useBffRequest(BffApiService.fetchIndustriesAndOccupations);
  const { request: verifyRequest, loading: verifyLoading, error: verifyError } = useBffApiRequest();
  const {
    request: updateVerifyRequest,
    loading: updateVerifyLoading,
    error: updateVerifyError,
  } = useBffApiRequest<BffUserAgcoVerifyUpdateResponse>();

  const initialValues: FormValues = {
    fullName: `${user.firstName} ${user.lastName}`,
    address: user.address,
    houseNumber: user.houseNumber || '',
    city: user.city,
    postCode: user.postalCode,
    occupationId: stringValueByKey(additionalUserDetails, 'occupationId'),
    industryId: stringValueByKey(additionalUserDetails, 'industryId'),
    mobile: {
      number: user.mobile,
      wholeNumber: user.mobilePrefix + user.mobile,
      prefix: user.mobilePrefix,
      country: user.countryCode,
    },
    isNotPepOrHio: !additionalUserDetails.isPep && !additionalUserDetails.isHio,
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    address: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    houseNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    city: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    postCode: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    occupationId: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    industryId: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
    isNotPepOrHio: Yup.boolean().notRequired(),
  });

  const mappedIndustries =
    fetchIndustriesAndOccupationsData?.industries.map(industry => ({
      value: industry.id.toString(),
      label: industry.name,
    })) ?? [];

  const mappedOccupations =
    fetchIndustriesAndOccupationsData?.occupations.map(occupation => ({
      value: occupation.id.toString(),
      label: occupation.name,
    })) ?? [];

  const handleOnLogout = () => {
    closeModal();
    triggerGTag(GTagEvents.reverification_logout);
    absoluteNavigate('/logout');
  };

  const handleOnSubmit = (values: FormValues) => {
    const isDataEqual = deepCompare(values, initialValues);
    if (isDataEqual) {
      verifyRequest({
        apiMethod: BffApiService.agcoVerify,
        successCallback: () => {
          dispatch(getConsents());
          closeModal();
          triggerGTag(GTagEvents.reverification_accepted);
        },
        errorCallback: (error: BffApiError) => {
          logException(error);
          triggerGTag(GTagEvents.reverification_error, {
            error: error.code,
          });
        },
      });
    } else {
      updateVerifyRequest({
        apiMethod: BffApiService.agcoVerifyUpdate,
        payload: {
          firstName: values.fullName.split(' ')[0],
          lastName: values.fullName.split(' ').slice(1).join(' '),
          mobilePrefix: values.mobile.prefix,
          mobileNumber: values.mobile.number,
          address: values.address,
          houseNumber: values.houseNumber ?? '',
          city: values.city,
          postalCode: values.postCode,
          occupationId: Number(values.occupationId),
          industryId: Number(values.industryId),
          isPepOrIsHio: !values.isNotPepOrHio,
        },
        successCallback: (data: BffUserAgcoVerifyUpdateResponse) => {
          if (!values.isNotPepOrHio) {
            dispatch(setShowUserIsPepOrHioLogout(true));
            handleOnLogout();
          } else {
            dispatch(getUserSuccess(data.user));
            dispatch(getConsentsSuccess(data.consents));
            closeModal();
            triggerGTag(GTagEvents.reverification_modified);
          }
        },
        errorCallback: error => {
          logException(error);
          triggerGTag(GTagEvents.reverification_error, {
            error: error.code,
          });
        },
      });
    }
  };

  useEffect(() => {
    if (isEditEnabled) {
      fetchIndustriesOccupationsRequest({
        axiosConfig: { params: { regulator: 'agco' } },
      });
    }
  }, [isEditEnabled]);

  return (
    <>
      <span
        onClick={() => setIsEditEnabled(true)}
        className="text-blue-light font-semibold cursor-pointer block w-fit ml-auto underline"
      >
        {intl.formatMessage({ id: 'detailsReverification.edit' })}
      </span>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="[&_input]:bg-white [&_.special-label]:!text-sm">
            <FormInput
              name="fullName"
              disabled={!isEditEnabled}
              label={intl.formatMessage({ id: 'inputs.fullName' })}
              placeholder={intl.formatMessage({ id: 'inputs.fullName' })}
            />
            <FormInput
              name="address"
              disabled={!isEditEnabled}
              label={intl.formatMessage({ id: 'inputs.address' })}
              type="text"
              autoComplete="address-line1"
              placeholder={intl.formatMessage({ id: 'inputs.address' })}
            />
            <FormInput
              name="houseNumber"
              disabled={!isEditEnabled}
              label={intl.formatMessage({ id: 'inputs.houseNumber' })}
              type="text"
              autoComplete="address-line2"
              placeholder={intl.formatMessage({ id: 'inputs.houseNumber' })}
            />
            <FormInput
              name="city"
              disabled={!isEditEnabled}
              label={intl.formatMessage({ id: 'inputs.city' })}
              type="text"
              autoComplete="address-level2"
              placeholder={intl.formatMessage({ id: 'inputs.city' })}
            />
            <FormInput
              name="postCode"
              disabled={!isEditEnabled}
              label={intl.formatMessage({ id: 'inputs.postcode' })}
              type="text"
              autoComplete="postal-code"
              placeholder={intl.formatMessage({ id: 'inputs.postcode' })}
            />
            <Field name="mobile">
              {({ field, form }: FieldProps) => (
                <MobileInput disabled={!isEditEnabled} field={field} form={form} {...field} />
              )}
            </Field>
            <FormSelect
              name="occupationId"
              label={intl.formatMessage({ id: 'inputs.occupation' })}
              isLoading={fetchIndustriesAndOccupationsLoading}
              options={mappedOccupations}
              defaultValue={{
                value: stringValueByKey(additionalUserDetails, 'occupationId'),
                label: additionalUserDetails.occupation || '',
              }}
              disabled={!isEditEnabled}
            />
            <FormSelect
              name="industryId"
              label={intl.formatMessage({ id: 'inputs.industry' })}
              isLoading={fetchIndustriesAndOccupationsLoading}
              options={mappedIndustries}
              defaultValue={{
                value: stringValueByKey(additionalUserDetails, 'industryId'),
                label: additionalUserDetails.industry || '',
              }}
              disabled={!isEditEnabled}
            />
            <FormCheckbox
              name="isNotPepOrHio"
              label={intl.formatMessage({ id: 'inputs.isNotPepOrHio' })}
              disabled={!isEditEnabled}
            />
            <BtnOneLineBlue
              type="submit"
              text={intl.formatMessage({ id: 'detailsReverification.confirm' })}
              isLoading={verifyLoading || updateVerifyLoading}
              className="py-0 h-12 min-h-fit mb-3 mt-6"
            />
          </Form>
        )}
      </Formik>
      <button
        className={classNames(
          'h-12 w-full rounded-full bg-gray-200 px-4 text-center font-bold transition hover:bg-gray-300',
          { disabled: verifyLoading || updateVerifyLoading },
        )}
        type="button"
        onClick={handleOnLogout}
      >
        {intl.formatMessage({ id: 'detailsReverification.logout' })}
      </button>
      {(verifyError || updateVerifyError) && (
        <div className="[&_p]:!text-base mt-4">
          <PromptMessage
            type="error"
            message={generateBffErrorMessageWithCode(
              intl.formatMessage({ id: 'error.support' }),
              verifyError ? verifyError.code : updateVerifyError!.code,
            )}
          />
        </div>
      )}
    </>
  );
};

export default DetailsReverificationForm;
